import { showNotification } from '../util/notifications'
import { useModalStack } from '../ui/ModalStack'
import useQueueTrack from '../hooks/useQueueTrack'
import { hoursMinsSecs } from '../util/time'
import SearchTrackControls from '../search/SearchTrackControls'
import TracksForAlbum from '../graphql/queries/TracksForAlbum'
import { useQuery } from '@apollo/client/react'
import useCurrentRoom from '../hooks/useCurrentRoom'
import { Box, Stack, Table, Text, Group, Button, Loader, Center } from '@mantine/core'

export default function AlbumView(props) {

    const { pushModal } = useModalStack()
    const { albumData: sourceTrack } = props
    const currentRoom = useCurrentRoom()

    // Extract values safely with defaults
    const artist = sourceTrack?.artist
    const album = sourceTrack?.album
    const trackRoomId = sourceTrack?.roomId

    // Always use current room for queueing, but query based on track's room or current room
    const queryRoomId = trackRoomId || currentRoom?.id
    const queueTrack = useQueueTrack(currentRoom?.id)

    const { data, loading } = useQuery(TracksForAlbum, {
        variables: { roomId: queryRoomId, artist: artist || '', album: album || '' },
        skip: !sourceTrack || !artist || !album
    })
    const albumData = data?.tracksForAlbum

    // Add null check for sourceTrack AFTER all hooks
    if (!sourceTrack) {
        return (
            <Center p="xl">
                <Loader size="md" />
            </Center>
        )
    }

    function sortedTracks() {
        return albumData?.map( t => t ).sort((a, b) => (Number(trackNumber(a)) < Number(trackNumber(b))) ? -1 : 1 ) ?? []
    }

    function trackNumber(track) {
        return track.track?.split(/\//)[0] ?? ''
    }

    function canQueueTracks() {
        // Check if track belongs to a different room
        return !(trackRoomId && trackRoomId !== currentRoom?.id)
    }

    async function queueAll() {
        for (const track of sortedTracks()) {
            await queueTrack(track, true)
        }
        showNotification({ content: <span>Added all tracks from <b>{ sourceTrack.album }</b></span> })
    }

    if (loading) return (
        <Center p="xl">
            <Loader size="md" />
        </Center>
    )

    const tracks = sortedTracks()

    return (
        <Box p="md">
            <Stack gap="md">
                {/* Album Header */}
                <Box
                    p="md"
                    style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Group gap="xs" align="center" wrap="wrap">
                        <Text size="lg" fw={700}>{sourceTrack.album}</Text>
                        <Text size="sm" c="dimmed">by</Text>
                        <Text size="lg" fw={500}>{sourceTrack.artist}</Text>
                    </Group>
                </Box>

                {/* Queue All Button */}
                {canQueueTracks() && (
                    <Group justify="flex-end">
                        <Button
                            leftSection={<span className="material-symbols-outlined">playlist_add</span>}
                            variant="light"
                            onClick={queueAll}
                        >
                            Queue All
                        </Button>
                    </Group>
                )}

                {/* Track List */}
                <Box
                    style={{
                        overflowY: 'auto',
                        maxHeight: 'calc(80vh - 250px)'
                    }}
                >
                    <Table highlightOnHover>
                        <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--mantine-color-dark-7)', zIndex: 1 }}>
                            <Table.Tr>
                                <Table.Th style={{ width: '60px', textAlign: 'center' }}>#</Table.Th>
                                <Table.Th style={{ textAlign: 'left' }}>Title</Table.Th>
                                <Table.Th style={{ width: '100px', textAlign: 'center' }}>Duration</Table.Th>
                                <Table.Th style={{ width: '120px', textAlign: 'center' }}>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {tracks.map((track, index) => (
                                <Table.Tr key={`${track.hash || track.id}-${index}`}>
                                    <Table.Td style={{ width: '60px', textAlign: 'center' }}>
                                        <Text size="sm" c="dimmed">{trackNumber(track)}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'left' }}>
                                        <Text
                                            size="sm"
                                            c="blue"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => pushModal('track', { trackId: track.id })}
                                        >
                                            {track.title || '[ untitled ]'}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td style={{ width: '100px', textAlign: 'center' }}>
                                        <Text size="sm" c="dimmed">{hoursMinsSecs(track.duration)}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ width: '120px', textAlign: 'center' }}>
                                        <SearchTrackControls track={track} />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Box>
            </Stack>
        </Box>
    )
}
